import { useController } from 'react-hook-form'
import type { UseFormReturn } from 'react-hook-form'
import { ports } from '@/features/consignment-form/contract'
import { fieldLabels } from '@/features/consignment-form/labels'
import { aYearFromToday, today } from '@/features/consignment-form/rules'
import type { Consignment } from '@/features/consignment-form/schema'
import { t } from '@/lang'
import { DatePicker } from '@/ui/DatePicker'
import { ChangeDirectionIcon } from '@/ui/icons/ChangeDirectionIcon'
import { IconButton } from '@/ui/IconButton'
import { FormField } from '@/ui/FormField'
import { Section } from '@/ui/Section'
import { testIds } from '@/lib/test-ids'

export function ShipmentSection({ form }: { form: UseFormReturn<Consignment> }) {
  const { control, formState, setValue, watch } = form
  const { field: readyFrom } = useController({ name: 'readyFrom', control })
  const [departure, destination] = watch(['departurePort', 'destinationPort'])
  const from = ports[departure]
  const to = ports[destination]

  return (
    <Section title={t('shipment.title')}>
      <div className="grid gap-5 md:grid-cols-2 md:gap-7.5 lg:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <span id="direction-label" className="text-label text-neutral-800">
            {fieldLabels.departurePort}
            <span aria-hidden="true" className="ms-1 text-error-600">
              *
            </span>
            <span className="sr-only">{t('common.required')}</span>
          </span>
          <div className="flex items-center gap-4">
            <p
              id="direction-route"
              data-testid={testIds.consignmentForm.direction}
              className="text-h2 text-primary-700"
            >
              {from} <span aria-hidden="true">→</span>
              <span className="sr-only">{t('shipment.route-to')}</span> {to}
            </p>
            <IconButton
              label={t('shipment.swap', { port: from })}
              aria-describedby="direction-label direction-route"
              onClick={() => {
                setValue('departurePort', destination, { shouldDirty: true })
                setValue('destinationPort', departure, { shouldDirty: true })
              }}
            >
              <ChangeDirectionIcon className="w-4" />
            </IconButton>
          </div>
        </div>
        <FormField
          htmlFor="ready-from"
          label={fieldLabels.readyFrom}
          error={formState.errors.readyFrom?.message}
          required
        >
          <DatePicker
            id="ready-from"
            min={today()}
            max={aYearFromToday()}
            value={readyFrom.value}
            onChange={readyFrom.onChange}
            onBlur={readyFrom.onBlur}
          />
        </FormField>
      </div>
    </Section>
  )
}
